// URL-state helpers for the Tasks page filters / sort / tab.
//
// The app does NOT use React Router. We sync filter state to and from
// `window.location.search` directly via `URLSearchParams` and
// `history.replaceState`, so the URL becomes the source of truth for
// shareable filtered views without dragging in a router. Wrapped in
// try/catch so jsdom or restricted environments can't crash the page.

export const SORT_FIELDS = ['dueDate', 'priority', 'status', 'assignee', 'age']

// Assignee filter is a list of chip ids: 'me', 'others'.
// `null` means no constraint (matches the universal "all chips off" rule).
const ASSIGNEE_VALUES = new Set(['me', 'others'])

export const DEFAULT_TASKS_URL_STATE = {
  tab: 'open',
  statuses: null,
  priorities: null,
  assignee: null,
  sortField: 'dueDate',
  sortDirection: 'asc',
}

function safeReadParams() {
  try {
    return new URLSearchParams(window.location.search)
  } catch {
    return new URLSearchParams('')
  }
}

function parseList(raw) {
  if (!raw) return null
  const parts = raw.split(',').map((p) => p.trim()).filter(Boolean)
  return parts.length > 0 ? parts : null
}

function parseAssignee(raw) {
  if (!raw) return null
  // Legacy single-value compat: `?assignee=me|others|all` used by older links.
  if (raw === 'me') return ['me']
  if (raw === 'others') return ['others']
  if (raw === 'all') return null
  const list = parseList(raw)?.filter((v) => ASSIGNEE_VALUES.has(v)) ?? null
  if (!list || list.length === 0) return null
  return list
}

function parseSort(raw) {
  if (!raw) return { sortField: null, sortDirection: 'asc' }
  const direction = raw.endsWith('-desc') ? 'desc' : 'asc'
  const field = raw.replace(/-(asc|desc)$/, '')
  if (!SORT_FIELDS.includes(field)) {
    return { sortField: null, sortDirection: 'asc' }
  }
  return { sortField: field, sortDirection: direction }
}

export function readTasksUrlState() {
  const params = safeReadParams()
  const { sortField, sortDirection } = parseSort(params.get('sort'))
  return {
    tab: params.get('tab') === 'past' ? 'past' : 'open',
    statuses: parseList(params.get('status')),
    priorities: parseList(params.get('priority')),
    assignee: parseAssignee(params.get('assignee')),
    sortField: sortField ?? DEFAULT_TASKS_URL_STATE.sortField,
    sortDirection,
  }
}

function setOrDelete(params, key, value) {
  if (value === null || value === undefined || value === '') {
    params.delete(key)
  } else {
    params.set(key, value)
  }
}

function serializeAssignee(list) {
  if (!list || list.length === 0) return null
  // Me + others together == unconstrained — drop the param.
  if (list.includes('me') && list.includes('others')) return null
  return list.join(',')
}

export function writeTasksUrlState(state) {
  try {
    const params = safeReadParams()
    setOrDelete(params, 'tab', state.tab === 'past' ? 'past' : null)
    setOrDelete(params, 'status', state.statuses?.join(',') || null)
    setOrDelete(params, 'priority', state.priorities?.join(',') || null)
    setOrDelete(params, 'assignee', serializeAssignee(state.assignee))
    // Legacy params that are no longer part of the filter state — drop them
    // from any URL that still carries them so the new page stays clean.
    params.delete('q')
    params.delete('pair')
    const sort = state.sortField && state.sortField !== DEFAULT_TASKS_URL_STATE.sortField
      ? `${state.sortField}-${state.sortDirection === 'desc' ? 'desc' : 'asc'}`
      : (state.sortDirection === 'desc' ? `${DEFAULT_TASKS_URL_STATE.sortField}-desc` : null)
    setOrDelete(params, 'sort', sort)
    const qs = params.toString()
    const newUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname
    window.history.replaceState({}, '', newUrl)
  } catch {
    // Ignore — URL is a UX nicety, not a correctness requirement.
  }
}
